/**
 * Type narrowing helper to eliminate `undefined` from `T | undefined` types
 * @param item - an item of any type
 * @returns boolean
 */
export function isDefined<T>(item: T | undefined): item is T {
  return item !== undefined;
}

/**
 * Type narrowing helper to eliminate `T` from `T | undefined` types
 * @param item - an item of any type
 * @returns boolean
 */
export function isUndefined<T>(item: T | undefined): item is undefined {
  return item === undefined;
}

/**
 * Type narrowing helper to elminate 'T' from 'T | null' types
 * @param item an item of any type
 * @returns boolean
 */
export function isNull<T>(item: T | null): item is null {
  return item === null;
}

/**
 * Type narrowing helper to elminate 'null' from 'T | null' types
 * @param item an item of any type
 * @returns boolean
 */
export function isNotNull<T>(item: T | null): item is T {
  return item !== null;
}

export function doesExist<T>(item: T | undefined | null): item is T {
  return isDefined(item) && isNotNull(item);
}

/**
 * Extracts the type of elements in a read-only array
 */
export type ElementOf<ReadonlyArrayType extends ReadonlyArray<any>> =
  ReadonlyArrayType extends ReadonlyArray<infer ElementType> ? ElementType : never;

/**
 * Makes everything within an interface or object type partial -- including the
 * types of all children
 * https://stackoverflow.com/questions/39713349/make-all-properties-within-a-typescript-interface-optional
 */
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

/**
 * Omits properties from a base type using value types
 *
 * Example:
 *
 * OmitByValueType<{ a: number, b: boolean, c: string }, string | number>   ===>   { b: boolean }
 */
export type OmitByValueType<BaseType, ValueType> = Omit<
  BaseType,
  {
    [Property in keyof BaseType]: BaseType[Property] extends ValueType ? Property : never;
  }[keyof BaseType]
>;

export const isNumber = (value: unknown): value is number => typeof value === 'number';

export const isString = (value: unknown): value is string => typeof value === 'string';

export const isBoolean = (value: unknown): value is boolean => typeof value === 'boolean';

export const isNonEmptyArray = <ElementT>(value: unknown): value is Array<ElementT> =>
  Array.isArray(value) && value.length > 0;

export const isNumberArray = (value: unknown): value is number[] =>
  Array.isArray(value) && value.every(isNumber);

export const ensureNumber = (value: unknown, defaultValue = 0): number =>
  isNumber(value) ? value : defaultValue;

/**
 * Converts an unknown value into an enum value. If the input value does not
 * conform to the input enum, then a default value is returned.
 */

const defaultComparator =
  <EnumValueType>(value: unknown) =>
  (enumValue: EnumValueType) =>
    enumValue === value;
export const ensureEnum = <EnumType, EnumValueType extends EnumType[keyof EnumType]>(
  value: unknown,
  fromEnum: EnumType,
  defaultValue: EnumValueType,
  comparer = defaultComparator,
): EnumValueType => {
  if (!fromEnum) {
    return defaultValue;
  }

  const enumValues: EnumValueType[] = Object.values(fromEnum);
  const compareToEnum = comparer<EnumValueType>(value);
  return enumValues.find(compareToEnum) ?? defaultValue;
};

/**
 * Can be used to check if a value is a valid enum value
 */
export const isValidEnumValue = <EnumType, EnumValueType extends EnumType[keyof EnumType]>(
  value: unknown,
  fromEnum: EnumType,
): value is EnumValueType => {
  if (!fromEnum) {
    return false;
  }

  const enumValues: EnumValueType[] = Object.values(fromEnum);
  return !!enumValues.find((enumValue) => enumValue === value);
};

/**
 * Can be used to check if a string is a valid key of `fromObject`
 */
export const isValidKey = <KeyType extends string>(
  key: string,
  fromObject: Record<KeyType, unknown>,
): key is KeyType => Object.keys(fromObject).includes(key);

/**
 * A type that represents a value that may be a given type or a default type
 * @template T - the type of the value
 * @template Default - the value's default type
 */
export type Maybe<T, Default = undefined> = T | Default;

/**
 * A type that represents a value that may be a given type or null
 * @template T - the type of the value
 */
export type Nullable<T> = Maybe<T, null>;

/**
 * A type that represents a value that may be a given type, null, or undefined
 * @template T - the type of the value
 */
export type Nullish<T> = Maybe<Nullable<T>>;

/** Possible object key types */
export type ObjectKey = string | number | symbol;

/**
 * A function with any number of arguments and an arbitrary return type
 * @template A - the function's arguments types
 * @template R - the function's return type
 */
export type AnyFunction<A extends any[] = any[], R = void> = (...args: A) => R;

/**
 * Changes a function's return type to a given type
 *
 * @template T - function type definition
 * @template R - function's return type
 *
 * @example
 * type DoTheThing = (a: number, b: string) => boolean; // -> (a: number, b: string) => boolean
 * type DoTheThingAndReturnNumber = ReplaceReturnType<DoTheThing, number>; // -> (a: number, b: string) => number
 */
export type ReplaceReturnType<T, R = void> = T extends (...args: infer A) => any
  ? (...args: A) => R
  : never;

/**
 * Require at least one key in a given object
 * @template T - the object type
 * @template Keys - the keys you want to require from
 */
export type RequireAtLeastOneKey<T, Keys extends keyof T = keyof T> = Keys extends keyof T
  ? Omit<T, Keys> & { [K in Keys]-?: Required<Pick<T, K>> & Partial<Omit<T, K>> }[Keys]
  : never;

/**
 * Makes specified object properties optional
 * @see https://stackoverflow.com/a/54178819
 */
export type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

/**
 * Excludes properties from `T` that are also present in `U`
 *
 * Example:
 * ```ts
 * type A = { a: number; b: string; c: boolean };
 * type B = { b: string; c: boolean; d: Date };
 * type C = ExcludeProperties<A, B>; // { a: number }
 * ```
 * @template T - the base type
 * @template U - the type containing properties to exclude from `T`
 */
export type ExcludeProperties<T, U> = Omit<T, keyof U>;
